import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchUsers, deleteUser, editUser, setUsersSearchAndSelect } from '../../redux/actions/users'
import { Grid, makeStyles, Typography, Button } from '@material-ui/core'
import { UserModel } from '../../shared/types/user'
import { useTranslation } from 'react-i18next'
import UserList from './userList/UserList'
import UserCard from './userCard/UserCard'
import { useCalcHeightToBottom } from '../../utils/ui'
import UserAddEditDialog from './userAddEditDialog/UserAddEditDialog'
import { DeleteConfirmationDialog } from '../../shared/components/deleteConfirmationDialog'
import { config } from '../../config'
import UserCarEditDialog from './userCarEditDialog/UserCarEditDialog'
import { fetchTenants } from '../../redux/actions/dashboard'
import { fetchAllUserTags } from '../../redux/actions/tags'

const useStyles = makeStyles((theme) => ({
    headerWrapper: {
        padding: theme.spacing(1),
    },
    actionButtonWrapper: {
        padding: theme.spacing(1, 0),
        textAlign: 'right',
    },
    listWrapper: {
        paddingTop: theme.spacing(2),
    },
    cardWrapper: {
        paddingTop: theme.spacing(2),
        width: '100%',
    },
}))

export default function Users() {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [selectedUser, setSelectedUser] = useState<UserModel>()

    // 1. Lokálny stav pre vyhľadávanie
    const [search, setSearch] = useState('')
    // Lokálny (frontend-only) stav pre filter podľa stavu (nepotvrdení / blokovaní)
    const [statusFilter, setStatusFilter] = useState<string[]>([])

    const [addEditDialogState, setAddEditDialogState] = useState({
        open: false,
        isAdd: true,
        onClose: (user: UserModel) => { },
        initialModel: undefined as UserModel | undefined,
    })
    const [carEditDialogState, setCarEditDialogState] = useState({
        open: false,
        onClose: (user: UserModel) => { },
        initialModel: undefined as UserModel | undefined,
    });

    const [refreshTrigger, setRefreshTrigger] = useState(0)
    const [userToDelete, setUserToDelete] = useState<UserModel>()

    const usersState = useSelector((state: any) => state.users.items)
    const users = useSelector((state: any) => state.users.items.value) ?? []
    const deletedUser = useSelector((state: any) => state.users.delete.value)
    const userDeleteExecuted = useSelector((state: any) => state.users.delete.executed)
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)

    const reduxUserSearch = useSelector((state: any) => state.users?.search?.value)

    const userCardHeightFill = useCalcHeightToBottom('userCardWrapper')

    const openAddDialog = () => {
        setAddEditDialogState({
            open: true,
            isAdd: true,
            onClose: onAddDialogClose,
            initialModel: undefined,
        })
    }

    const onAddDialogClose = (user?: UserModel) => {
        setAddEditDialogState(prev => ({ ...prev, open: false }))
        if (user) {
            setRefreshTrigger(prev => prev + 1)
        }
    }

    const onEditDialogClose = (user?: UserModel) => {
        setAddEditDialogState(prev => ({ ...prev, open: false }))
        if (user) {
            setRefreshTrigger(prev => prev + 1)
        }
    }

    const onEditCarsDialogClose = (user?: UserModel) => {
        setCarEditDialogState(prev => ({ ...prev, open: false }))
        if (user) {
            setRefreshTrigger(prev => prev + 1)
        }
    }

    const onUserSelected = (user: UserModel) => {
        setSelectedUser(user)
    }

    const onUserDelete = (user: UserModel) => {
        setUserToDelete(user)
    }

    const onUserEdit = (user: UserModel) => {
        setAddEditDialogState({
            open: true,
            isAdd: false,
            onClose: onEditDialogClose,
            initialModel: user,
        })
    }

    const onCarsEdit = (user: UserModel) => {
        setCarEditDialogState({
            open: true,
            onClose: onEditCarsDialogClose,
            initialModel: user,
        })
    }

    const handleRemoveUser = async () => {
        const userToDeleteDefined = userToDelete as UserModel
        if (tenantId && tenantId === config.mainTenantId) {
            await dispatch(deleteUser(userToDeleteDefined))
        } else {
            const userTenantArr = userToDeleteDefined.tenants.split(',');
            let filteredTenants: string[] = [];
            userTenantArr.forEach(element => {
                if (element !== tenantId) {
                    filteredTenants.push(element)
                }
            });
            const newTenants = filteredTenants.join(', ');
            userToDeleteDefined.tenants = newTenants;
            userToDeleteDefined.tags = [];

            await dispatch(editUser(userToDeleteDefined))
            dispatch(fetchUsers('', tenantId))
        }
        setUserToDelete(undefined)
    }

    useEffect(() => {
        if (tenantId) {
            dispatch(fetchUsers('', tenantId))
        }
    }, [dispatch, tenantId, refreshTrigger, deletedUser])

    const filteredUsers = useMemo(() => {
        const searchLower = search.toLowerCase().trim();

        const searchFiltered = !searchLower
            ? users
            : users.filter((user: UserModel) => {
                  const simpleMatches =
                      (user.firstName ?? '').toLowerCase().includes(searchLower) ||
                      (user.lastName ?? '').toLowerCase().includes(searchLower) ||
                      (user.email ?? '').toLowerCase().includes(searchLower) ||
                      (user.phone ?? '').toLowerCase().includes(searchLower);

                  const rfidMatches = user.rfids?.some((rfid) =>
                      rfid.rfid.toLowerCase().includes(searchLower)
                  ) ?? false;

                  const carMatches = user.cars?.some((car) =>
                      (car.carLicensePlate ?? '').toLowerCase().includes(searchLower)
                  ) ?? false;

                  return simpleMatches || rfidMatches || carMatches;
              });

        if (statusFilter.length === 0) return searchFiltered;

        return searchFiltered.filter((user: UserModel) => {
            return (
                (!statusFilter.includes('unconfirmed') || !user.confirmed) &&
                (!statusFilter.includes('blocked') || user.blocked) &&
                (!statusFilter.includes('monthlyBilling') || (user.monthlyAllowance ?? 0) > 0)
            );
        });
    }, [users, search, statusFilter]);

    useEffect(() => {
        if (usersState.executed) {
            if (filteredUsers && filteredUsers.length > 0) {
                if (selectedUser != null) {
                    const userToSelect = filteredUsers.find((x: any) => x.id === selectedUser.id)
                    if (userToSelect != null) {
                        setSelectedUser(userToSelect)
                    } else {
                        setSelectedUser(filteredUsers[0])
                    }
                } else {
                    setSelectedUser(filteredUsers[0])
                }
            } else {
                setSelectedUser(undefined)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredUsers, usersState.executed])

    useEffect(() => {
        if (reduxUserSearch) {
            setSearch(reduxUserSearch) // Nastaví lokálny useState search na prenesenú hodnotu

            // Voliteľné: Ak chceš po prenesení vyčistiť Redux stav, aby pri bežnom prechode 
            // na /users neskôr nezostalo vyhľadávanie "zaseknuté" na starom mene:
            dispatch(setUsersSearchAndSelect(''))
        }
    }, [reduxUserSearch, dispatch])

    useEffect(() => {
        dispatch(fetchTenants())
        dispatch(fetchAllUserTags())
    }, [dispatch])

    return (
        <>
            <Grid container spacing={0}>
                <Grid item sm={2} className={classes.headerWrapper}>
                    <Typography align="left" variant="h3">
                        {t('users.title')}
                    </Typography>
                </Grid>
                <Grid item sm={10} className={classes.actionButtonWrapper}>
                    <Button
                        onClick={openAddDialog}
                        variant="contained"
                        color="primary"
                    >
                        {t('users.newUser')}
                    </Button>
                    {addEditDialogState.open && <UserAddEditDialog {...addEditDialogState} />}
                    {carEditDialogState.open && <UserCarEditDialog {...carEditDialogState} />}
                </Grid>
                <Grid item sm={4} className={classes.listWrapper}>
                    {/* Posielame search props presne rovnako ako pri tagoch */}
                    <UserList
                        selectedUser={selectedUser}
                        users={filteredUsers}
                        search={search}
                        onSearchChange={setSearch}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                        onUserSelected={onUserSelected}
                    />
                </Grid>
                <Grid item sm={8} className={classes.cardWrapper}>
                    <div id="userCardWrapper" ref={userCardHeightFill}>
                        {selectedUser && (
                            <UserCard
                                user={selectedUser}
                                onDelete={() => onUserDelete(selectedUser)}
                                onEdit={() => onUserEdit(selectedUser)}
                                onEditCars={() => onCarsEdit(selectedUser)}
                            />
                        )}
                    </div>
                </Grid>
            </Grid>
            <DeleteConfirmationDialog
                open={Boolean(userToDelete)}
                onConfirm={handleRemoveUser}
                onCancel={() => setUserToDelete(undefined)}
                inProgress={!userDeleteExecuted}
            />
        </>
    )
}